import {
  createVendorBill,
  payVendorBill,
  undoVendorBillPayment,
  useGetVendorBillSummary,
  useListVendorBills,
  useListWorkOrders,
  voidVendorBill,
  type BillPaymentMethod,
  type VendorBillOut,
} from '@myrentalmate/api-client';
import { Button, Chip, Stack, TextField, Typography, MenuItem } from '@mui/material';
import { useState } from 'react';

import { DataTable, type DataColumn } from '../../components/DataTable';
import { FormDialog } from '../../components/FormDialog';
import { optional, required, type Field } from '../../components/FormFields';
import { StatRow, StatTile } from '../../components/Stats';
import { okData, okItems } from '../../lib/apiData';
import { formatDate, formatMoney } from '../../lib/format';
import { useCan } from '../../lib/permissions';
import { BILL_METHOD, BILL_STATUS, optionsOf } from './labels';
import { useExpenseOptions, useVendorOptions, useWherePicker } from './usePickers';
import { TablePager, useTablePage } from '../../components/TablePager';

const REASON: Field[] = [{ name: 'reason', label: 'Reason', required: true }];
const PAY_FIELDS: Field[] = [
  { name: 'paid_on', label: 'Paid On', type: 'date', required: true },
  {
    name: 'method',
    label: 'Paid By',
    type: 'select',
    required: true,
    defaultValue: 'check',
    options: optionsOf(BILL_METHOD),
  },
  { name: 'reference', label: 'Check or Confirmation Number' },
];

const billCount = (count: number) => `${count} ${count === 1 ? 'bill' : 'bills'}`;

type Dialog = { kind: 'add' } | { kind: 'pay' | 'undo' | 'void'; bill: VendorBillOut } | null;

const STATUS_FILTER = [
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'paid', label: 'Paid' },
  { value: 'void', label: 'Void' },
  { value: '', label: 'All' },
];

export function BillsPage() {
  const canManage = useCan('bills.manage');
  const [status, setStatus] = useState<'unpaid' | 'paid' | 'void' | ''>('unpaid');
  const [q, setQ] = useState('');
  const [dialog, setDialog] = useState<Dialog>(null);
  const paging = useTablePage();
  const billQuery = useListVendorBills({
    q,
    ...paging.params,
    ...(status ? { bill_status: status } : {}),
  });
  const bills = okItems(billQuery.data);
  const summary = okData(useGetVendorBillSummary().data);
  const where = useWherePicker();
  const vendors = useVendorOptions();
  const accounts = useExpenseOptions();
  const orders = okItems(useListWorkOrders({ page_size: 100 }).data);

  const addFields: Field[] = [
    { name: 'vendor_id', label: 'Vendor', type: 'select', required: true, options: vendors },
    { name: 'where', label: 'For', type: 'select', required: true, options: where.options },
    {
      name: 'work_order_id',
      label: 'Work Order',
      type: 'select',
      options: [
        { value: '', label: 'None' },
        ...orders.map((o) => ({ value: String(o.id), label: `${o.title}, ${o.place}` })),
      ],
    },
    { name: 'bill_number', label: "Vendor's Invoice Number" },
    { name: 'bill_date', label: 'Bill Date', type: 'date', required: true },
    { name: 'due_date', label: 'Due Date', type: 'date' },
    { name: 'amount', label: 'Amount', type: 'number', required: true },
    {
      name: 'account_code',
      label: 'Expense Account',
      type: 'select',
      required: true,
      defaultValue: '5000',
      options: accounts,
    },
    { name: 'memo', label: 'Note' },
  ];

  const columns: DataColumn<VendorBillOut>[] = [
    { label: 'Vendor', render: (b) => b.vendor_name },
    {
      label: 'For',
      render: (b) => (
        <>
          {b.place}
          {b.work_order_title && (
            <Typography variant="body2" color="text.secondary">
              {b.work_order_title}
            </Typography>
          )}
        </>
      ),
    },
    { label: 'Invoice', render: (b) => b.bill_number ?? '–' },
    { label: 'Bill date', render: (b) => formatDate(b.bill_date) },
    { label: 'Due', render: (b) => (b.due_date ? formatDate(b.due_date) : '–') },
    { label: 'Expense', render: (b) => b.account_name },
    { label: 'Amount', align: 'right', render: (b) => formatMoney(b.amount) },
    {
      label: 'Status',
      render: (b) => (
        <Chip
          size="small"
          color={BILL_STATUS[b.status].color}
          label={BILL_STATUS[b.status].label}
          title={
            b.paid_on
              ? `Paid ${formatDate(b.paid_on)}${b.payment_reference ? `, ${b.payment_reference}` : ''}`
              : undefined
          }
        />
      ),
    },
    ...(canManage
      ? [
          {
            label: 'Actions',
            align: 'right' as const,
            render: (b: VendorBillOut) => (
              <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                {b.status === 'unpaid' && (
                  <>
                    <Button size="small" onClick={() => setDialog({ kind: 'pay', bill: b })}>
                      Pay
                    </Button>
                    <Button size="small" onClick={() => setDialog({ kind: 'void', bill: b })}>
                      Void
                    </Button>
                  </>
                )}
                {b.status === 'paid' && (
                  <Button size="small" onClick={() => setDialog({ kind: 'undo', bill: b })}>
                    Undo payment
                  </Button>
                )}
              </Stack>
            ),
          },
        ]
      : []),
  ];

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flexGrow: 1 }}>
          <Typography variant="h5">Vendor Bills</Typography>
          <Typography color="text.secondary">
            What you owe vendors. Entering a bill books the expense, and paying it moves the cash.
          </Typography>
        </div>
        {canManage && (
          <Button variant="contained" onClick={() => setDialog({ kind: 'add' })}>
            Add bill
          </Button>
        )}
      </Stack>
      <StatRow>
        <StatTile
          label="Unpaid"
          value={summary && formatMoney(summary.unpaid_total)}
          hint={summary && billCount(summary.unpaid_count)}
        />
        <StatTile
          label="Past Due"
          value={summary && formatMoney(summary.overdue_total)}
          hint={summary && billCount(summary.overdue_count)}
        />
      </StatRow>
      <Stack direction="row" spacing={2}>
        <TextField
          size="small"
          label="Search"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            paging.setPage(0);
          }}
        />
        <TextField
          select
          size="small"
          label="Show"
          value={status}
          sx={{ minWidth: 140 }}
          onChange={(e) => {
            setStatus(e.target.value as typeof status);
            paging.setPage(0);
          }}
        >
          {STATUS_FILTER.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      {bills.length === 0 ? (
        <Typography color="text.secondary">No bills here.</Typography>
      ) : (
        <>
          <DataTable columns={columns} rows={bills} rowKey={(b) => b.id} />
          <TablePager paging={paging} total={okData(billQuery.data)?.total ?? 0} />
        </>
      )}
      {dialog?.kind === 'add' && (
        <FormDialog
          title="Add Bill"
          intro="This books the expense now. To fix a mistake later, void the bill and enter it again."
          fields={addFields}
          initial={{
            vendor_id: '',
            where: '',
            work_order_id: '',
            bill_number: '',
            bill_date: '',
            due_date: '',
            amount: '',
            account_code: '5000',
            memo: '',
          }}
          submitLabel="Add bill"
          onSubmit={(values) => {
            const order = optional(values, 'work_order_id');
            return createVendorBill({
              ...where.parse(required(values, 'where')),
              vendor_id: Number(required(values, 'vendor_id')),
              work_order_id: order ? Number(order) : null,
              bill_number: optional(values, 'bill_number'),
              bill_date: required(values, 'bill_date'),
              due_date: optional(values, 'due_date'),
              amount: required(values, 'amount'),
              account_code: required(values, 'account_code'),
              memo: optional(values, 'memo'),
            });
          }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'pay' && (
        <FormDialog
          title={`Pay ${dialog.bill.vendor_name} ${formatMoney(dialog.bill.amount)}`}
          intro="Record a payment you've made. This app doesn't send the money."
          fields={PAY_FIELDS}
          initial={{ paid_on: '', method: 'check', reference: '' }}
          submitLabel="Record payment"
          onSubmit={(values) =>
            payVendorBill(dialog.bill.id, {
              paid_on: required(values, 'paid_on'),
              method: required(values, 'method') as BillPaymentMethod,
              reference: optional(values, 'reference'),
            })
          }
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'undo' && (
        <FormDialog
          title="Undo This Payment"
          intro="The bill goes back to unpaid, and the cash comes back in the books."
          fields={REASON}
          initial={{ reason: '' }}
          submitLabel="Undo payment"
          onSubmit={(values) =>
            undoVendorBillPayment(dialog.bill.id, { reason: required(values, 'reason') })
          }
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'void' && (
        <FormDialog
          title="Void This Bill"
          intro="This reverses the expense. The bill stays on record, marked void."
          fields={REASON}
          initial={{ reason: '' }}
          submitLabel="Void bill"
          onSubmit={(values) =>
            voidVendorBill(dialog.bill.id, { reason: required(values, 'reason') })
          }
          onClose={() => setDialog(null)}
        />
      )}
    </Stack>
  );
}
